import { useState } from "react";
import { Button } from "@/components/ui/button";
import PageTitleScript from "@/components/MetaScript";
import { proPricing, proFeatures } from "@/config/subscription";
import { planApi } from "@/api/planApi";
import { LoadingModal } from "@/components/ui/LoadingModal";
import { useToastHandler } from "@/hooks/useToastHandler";
import { Crown } from "lucide-react";

export function SettingsSubscriptionFreeView() {
  const [selectedPlan, setSelectedPlan] = useState<"monthly" | "yearly">(
    "yearly"
  );
  const [isLoading, setIsLoading] = useState(false);
  const { handleError } = useToastHandler();

  const handleUpgrade = async () => {
    try {
      setIsLoading(true);
      const response = await planApi.createCheckout({
        plan_cycle: selectedPlan,
        plan_type: "pro",
      });

      // Redirect to checkout URL
      window.location.href = response.checkout_url;
    } catch (error) {
      handleError(error);
      setIsLoading(false);
    } finally {
      //setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-gray-900 to-black rounded-xl text-accent-fg-hot shadow-lg min-h-screen relative">
      <PageTitleScript title="Pro Access" />

      <div className="container mx-auto px-8 py-10 max-w-2xl pb-32">
        {/* Header */}

        <div className="flex items-center gap-3 mb-8">
          <div className=" w-8 h-8 bg-accent-bg-hot rounded-full flex items-center justify-center">
            <Crown className="w-5 h-5 text-accent-fg-hot" />
          </div>
          <h1 className="text-3xl font-bold text-accent-fg-hot">PRO Access</h1>
        </div>

        {/* Pro Features */}
        <div className="space-y-4 mb-12">
          {proFeatures.map((feature, index) => (
            <div key={index} className="flex flex-row items-center gap-1">
              <div className="w-6">
                <svg
                  className="w-4 h-4 text-accent-fg-hot"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </div>
              <p
                className="flex-1 text-base md:text-lg leading-tight"
                dangerouslySetInnerHTML={{ __html: feature }}
              ></p>
            </div>
          ))}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 gap-6 mb-8 -mx-4">
          {/* Yearly Plan */}
          <button
            onClick={() => setSelectedPlan("yearly")}
            className={`bg-slate-800/50 backdrop-blur rounded-2xl pl-6 pr-6 py-4 border-2 relative transition-all hover:bg-slate-700/50 ${
              selectedPlan === "yearly"
                ? "border-accent-bg-hot"
                : "border-slate-700"
            }`}
          >
            {selectedPlan === "yearly" && (
              <div className="absolute -top-3 right-4">
                <span className="bg-accent-bg-hot text-accent-fg-hot px-3 py-1 rounded-full text-sm font-medium">
                  {proPricing.yearly.discount}% off
                </span>
              </div>
            )}
            {selectedPlan === "yearly" && (
              <div className="absolute top-12 right-4">
                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-slate-900"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
            <h3 className="text-base font-medium text-gray-300 mb-1 text-left">
              Limited time offer
            </h3>
            <div className="text-left">
              <span className="text-2xl font-light text-gray-400 line-through mr-2">
                ${(proPricing.monthly.amount * 12).toFixed(2)}
              </span>

              <span className="text-2xl font-bold">
                ${proPricing.yearly.amount.toFixed(2)}
              </span>
              <span className="text-gray-400">/year</span>
            </div>
            <p className="text-gray-400 text-sm text-left">
              Equivalent to $2.5/month; billed yearly
            </p>
          </button>

          {/* Monthly Plan */}
          <button
            onClick={() => setSelectedPlan("monthly")}
            className={`bg-slate-800/50 backdrop-blur rounded-2xl pl-6 pr-6 py-4 border-2 relative transition-all hover:bg-slate-700/50 ${
              selectedPlan === "monthly"
                ? "border-accent-bg-hot"
                : "border-slate-700"
            }`}
          >
            {selectedPlan === "monthly" && (
              <div className="absolute top-8 right-4">
                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-slate-900"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
            <div className="text-left">
              <span className="text-2xl font-bold">
                ${proPricing.monthly.amount}
              </span>
              <span className="text-gray-400">
                /{proPricing.monthly.period}
              </span>
            </div>
            <p className="text-gray-400 text-sm text-left">
              Billed monthly; cancel anytime
            </p>
          </button>
        </div>
      </div>

      {/* Sticky Bottom Section */}
      <div className="fixed bottom-0 left-0 right-0  px-6">
        <div className="bg-gradient-to-r from-gray-800 to-black p-4">
          {/* Selected Plan Display */}
          <div className="mb-3 text-center">
            <span className="text-sm text-gray-300">Selected: </span>
            <span className="font-semibold text-accent-fg-hot">
              {selectedPlan === "yearly"
                ? `$${proPricing.yearly.amount.toFixed(2)}/year (${
                    proPricing.yearly.discount
                  }% off)`
                : `$${proPricing.monthly.amount}/month`}
            </span>
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleUpgrade}
            disabled={isLoading}
            className="w-full bg-accent-bg-hot hover:bg-accent-bg-hot/90 disabled:bg-accent-bg-hot/40 text-accent-fg-hot py-3 text-base font-medium rounded-md h-auto"
          >
            Activate PRO Features
          </Button>

          {/* Footer */}
          <div className="text-center text-accent-fg-hot text-xs mt-2">
            <span>
              Credit card protected by{" "}
              <a
                href="https://stripe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-fg-hot hover:text-white underline underline-offset-2"
              >
                Stripe
              </a>
            </span>
          </div>
        </div>
      </div>

      {/* Loading Modal */}
      <LoadingModal
        isOpen={isLoading}
        message="Redirecting to checkout"
        subMessage="This may take a few seconds..."
      />
    </div>
  );
}
